import React from "react";
import PropTypes from "prop-types";
import { ArrowRight, CalendarDays } from "lucide-react";
import BridgeText from "./BridgeText";
import { handleInternalLinkClick } from "../utils/navigation";
import { cleanBridgeText } from "../utils/bridgeText";

const MONTH_DAY_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  day: "numeric",
});

const formatReleaseLabel = (show = {}) => {
  if (typeof show.releaseDate !== "string") {
    return show.seasonNumber ? `Season ${show.seasonNumber}` : null;
  }

  const [year, month, day] = show.releaseDate.split("-").map(Number);
  if (!year || !month || !day) {
    return show.seasonNumber ? `Season ${show.seasonNumber}` : null;
  }

  const date = new Date(year, month - 1, day);
  const dateLabel = MONTH_DAY_FORMATTER.format(date);
  return show.seasonNumber ? `${dateLabel} · Season ${show.seasonNumber}` : dateLabel;
};

const MonthRecommendationRail = ({ pairings = [] }) => {
  const visiblePairings = Array.isArray(pairings) ? pairings.slice(0, 3) : [];
  if (visiblePairings.length < 2) {
    return null;
  }

  return (
    <section
      className="month-recommendation-module"
      aria-labelledby="month-recommendations-heading"
    >
      <div className="month-recommendation-module__intro">
        <h2
          id="month-recommendations-heading"
          className="text-h3 font-semibold leading-tight text-foreground"
        >
          What to watch after this month’s shows
        </h2>
        <p className="max-w-2xl text-body-small leading-relaxed text-muted-foreground">
          Coming up next for fans of shows from this month.
        </p>
      </div>

      <div className="month-recommendation-module__grid">
        {visiblePairings.map((pairing) => {
          const currentHref = `/show/${pairing.current.slug}`;
          const releaseLabel = formatReleaseLabel(pairing.current);
          const bridge = cleanBridgeText(pairing.bridge, pairing.anchor.title, pairing.current.title);
          const bridgePreview = bridge;

          return (
            <article
              key={`${pairing.anchor.slug}-${pairing.current.slug}`}
              className="min-w-[18rem] md:min-w-0"
            >
              <a
                href={currentHref}
                onClick={(event) => handleInternalLinkClick(event, currentHref)}
                className="month-recommendation-card group"
                aria-label={`Open ${pairing.current.title}, recommended if you liked ${pairing.anchor.title}`}
              >
                <div className="mb-3 flex items-center gap-2 text-body-small leading-snug text-muted-foreground">
                  {pairing.anchor.poster && (
                    <img
                      src={pairing.anchor.poster}
                      alt=""
                      loading="lazy"
                      className="h-10 w-7 shrink-0 rounded-sm border border-border/70 object-cover"
                    />
                  )}
                  <p className="min-w-0">
                    If you liked{" "}
                    <span className="font-semibold text-foreground">
                      {pairing.anchor.title}
                    </span>
                  </p>
                </div>

                <div className="flex gap-3">
                  {pairing.current.poster && (
                    <img
                      src={pairing.current.poster}
                      alt=""
                      loading="lazy"
                      className="h-16 w-11 shrink-0 rounded-md border border-border/70 object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="title-link line-clamp-2 text-body font-semibold leading-tight text-foreground">
                      {pairing.current.title}
                    </h3>
                    {releaseLabel && (
                      <p className="mt-1 inline-flex items-center gap-1 text-body-xs font-medium text-muted-foreground">
                        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                        {releaseLabel}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                </div>

                {bridgePreview && (
                  <p className="mt-3 border-t border-border/70 pt-2 text-body-small leading-relaxed text-foreground/75">
                    <BridgeText text={bridgePreview} titles={[pairing.anchor.title, pairing.current.title]} />
                  </p>
                )}
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
};

MonthRecommendationRail.propTypes = {
  pairings: PropTypes.arrayOf(
    PropTypes.shape({
      anchor: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        poster: PropTypes.string,
      }).isRequired,
      current: PropTypes.shape({
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        poster: PropTypes.string,
        releaseDate: PropTypes.string,
        seasonNumber: PropTypes.number,
      }).isRequired,
      bridge: PropTypes.string,
    })
  ),
};

export default MonthRecommendationRail;
